import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavigationService } from '../services/navigationService';

interface NavigationState {
  currentPage: string | null;
  previousPage: string | null;
  history: string[];
  canGoBack: boolean;
}

interface NavigationAction {
  type: string;
  payload?: any;
}

interface NavigationContextType {
  state: NavigationState;
  goBack: (fallbackPath?: string) => void;
  goToWithBack: (path: string, options?: any) => void;
}

// Navigation context state
const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

// Navigation actions
const NAVIGATION_ACTIONS = {
  ADD_TO_HISTORY: 'ADD_TO_HISTORY',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  CLEAR_HISTORY: 'CLEAR_HISTORY',
  SET_PREVIOUS_PAGE: 'SET_PREVIOUS_PAGE',
};

// Navigation reducer
const navigationReducer = (state: NavigationState, action: NavigationAction): NavigationState => {
  switch (action.type) {
    case NAVIGATION_ACTIONS.ADD_TO_HISTORY:
      return {
        ...state,
        history: [...state.history, action.payload].slice(-10), // Keep last 10 pages
      };
    
    case NAVIGATION_ACTIONS.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: action.payload,
      };
    
    case NAVIGATION_ACTIONS.SET_PREVIOUS_PAGE:
      return {
        ...state,
        previousPage: action.payload,
      };
    
    case NAVIGATION_ACTIONS.CLEAR_HISTORY:
      return {
        ...state,
        history: [],
        previousPage: null,
      };
    
    default:
      return state;
  }
};

// Initial state
const initialState: NavigationState = {
  currentPage: null,
  previousPage: null,
  history: [],
  canGoBack: false,
};

interface NavigationProviderProps {
  children: React.ReactNode;
}

// Navigation Provider Component
export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const [state, dispatch] = useReducer(navigationReducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();

  // Track page changes
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Set current page
    dispatch({ type: NAVIGATION_ACTIONS.SET_CURRENT_PAGE, payload: currentPath });
    
    // Store in session storage for persistence
    NavigationService.setPreviousPage(currentPath);
    
    // Add to history if it's a new page
    if (state.currentPage && state.currentPage !== currentPath) {
      dispatch({ type: NAVIGATION_ACTIONS.ADD_TO_HISTORY, payload: state.currentPage });
      dispatch({ type: NAVIGATION_ACTIONS.SET_PREVIOUS_PAGE, payload: state.currentPage });
    }
  }, [location.pathname, state.currentPage]);

  // Navigation methods
  const goBack = useCallback((fallbackPath = '/') => {
    // If we have URL parameters and we're on the same page, clear them instead of going back
    const hasURLParams = location.search && location.search.length > 1;
    
    if (hasURLParams) {
      // Check if we're on a store page with auditId parameter
      if (location.pathname.includes('/store/') && location.search.includes('auditId')) {
        // Clear only the auditId parameter and stay on the same page
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('auditId');
        navigate(location.pathname + (newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''), { replace: true });
        return;
      }
      
      // For other cases, clear all URL parameters
      navigate(location.pathname, { replace: true });
      return;
    }
    
    // If we have a previous page in our history, go back to it
    if (state.previousPage && state.previousPage !== location.pathname) {
      navigate(state.previousPage);
      return;
    }
    
    // If we have browser history, use it
    if (NavigationService.canGoBack()) {
      navigate(-1);
      return;
    }
    
    // Fallback to the provided path
    navigate(fallbackPath);
  }, [navigate, state.previousPage, location.pathname, location.search]);

  const goToWithBack = useCallback((path: string, options = {}) => {
    // Store current page before navigating
    if (location.pathname !== path) {
      dispatch({ type: NAVIGATION_ACTIONS.SET_PREVIOUS_PAGE, payload: location.pathname });
    }
    navigate(path, options);
  }, [navigate, location.pathname]);

  const contextValue: NavigationContextType = {
    state,
    goBack,
    goToWithBack,
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

// Custom hook to use navigation context
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}; 